import { FileUploadImportConfig } from '@oneschema/react';
import React, { useCallback, useState } from 'react';

import { useAscContext } from '@neo.tax/asc/context';
import { OneSchemaWrapper } from '@neo.tax/shared';

import { PayrollMonthExpenseInstructionModal } from '../payroll-month-expense-instruction-modal';
import { useAscCostUpload } from './hooks';

type AscCostUploadModalProps = {
  close: () => void;
  companyId: string;
  fiscalYearId: string;
  isOpen: boolean;
};

export const AscCostUploadModal = ({ close, companyId, isOpen }: AscCostUploadModalProps): React.ReactElement => {
  const { fiscalYearId, period, periodName } = useAscContext();
  const [hasLaunched, setHasLaunched] = useState<boolean>(false);
  const [hasConfirmedInstruction, setHasConfirmedInstruction] = useState<boolean>(false);

  const { handleSuccess, oneSchemaJwt, permanentUrl, signedUrl } = useAscCostUpload({
    companyId,
    fiscalYearId,
    isOpen,
    periodName,
  });

  const resetState = useCallback((): void => {
    close();
    setHasConfirmedInstruction(false);
    setHasLaunched(false);
  }, [close]);

  const onCancel = (): void => {
    resetState();
  };

  const onError = (): void => {
    resetState();
  };

  const onSuccess = async (): Promise<void> => {
    await handleSuccess();
    resetState();
  };

  const onLaunched = useCallback((): void => {
    // Close the period modal only after OneSchema has successfully launched
    setHasLaunched(true);
  }, []);

  const isOneSchemaOpen = isOpen && !!oneSchemaJwt && !!signedUrl && !!permanentUrl && hasConfirmedInstruction;

  const importConfig: FileUploadImportConfig = {
    format: 'csv',
    type: 'file-upload',
    url: signedUrl ?? '',
  };

  return (
    <>
      {!hasLaunched && (
        <PayrollMonthExpenseInstructionModal
          isOpen={isOpen}
          close={onCancel}
          loading={hasConfirmedInstruction}
          period={period}
          submit={() => setHasConfirmedInstruction(true)}
        />
      )}
      <OneSchemaWrapper
        isOpen={isOneSchemaOpen}
        userJwt={oneSchemaJwt ?? ''}
        templateKey="asc-cost-upload-v1"
        importConfig={importConfig}
        onCancel={onCancel}
        onError={onError}
        onSuccess={onSuccess}
        onLaunched={onLaunched}
      />
    </>
  );
};
